import { useDispatch, useSelector, useStore } from "react-redux"
import api from './botApi'
import {
    messagePosted,
    sessionsUpdated,
    currentUpdated,
    startStreamingResponse,
    appendStreamDelta,
    resetStreamBuffers,
    finalizeStreamingResponse,
    markStreamingFailed,
    sessionNameUpdated,
} from "./botSlice"
import { getCurrentConfig } from "../../config/currentBotConfig"
import { mapInteractionsToTranscript, mapSessionMetadata } from "./mapPlatformResponses"

const DESIGN_PREVIEW_LOCAL_SESSION = '__design_preview__'

/** Session ids with an in-flight background naming request. */
const namingInFlight = new Set()

function hasSessionName(name) {
    if (name == null) return false
    const normalizedName = String(name).trim()
    return normalizedName !== '' && normalizedName.toLowerCase() !== 'null'
}

function countCompletedModelTurns(transcript) {
    if (!Array.isArray(transcript)) return 0
    return transcript.filter(
        (entry) =>
            entry?.sender === 'MODEL' &&
            !entry.isStreaming &&
            !entry.errorMessage
    ).length
}

function formatTranscriptForNaming(transcript) {
    if (!Array.isArray(transcript)) return ''
    const lines = []
    for (const entry of transcript) {
        if (!entry?.text) continue
        if (entry.sender === 'USER') {
            lines.push(`משתמש: ${entry.text}`)
        } else if (entry.sender === 'MODEL' && !entry.isStreaming && !entry.errorMessage) {
            lines.push(`בוט: ${entry.text}`)
        }
    }
    return lines.join('\n')
}

/** Single sidebar row in admin design preview (no GET /sessions list). */
function buildDesignPreviewSessionStub(sessionId, config) {
    return {
        id: sessionId,
        botId: config?.botId,
        name: "תצוגת עיצוב",
        lastUpdated: new Date().toISOString(),
    }
}

function isLocalDesignPreviewSession(sessionId) {
    return sessionId === DESIGN_PREVIEW_LOCAL_SESSION
}

function dispatchDesignPreviewLocalSession(config, dispatch) {
    const previewStub = buildDesignPreviewSessionStub(DESIGN_PREVIEW_LOCAL_SESSION, config)
    dispatch(
        sessionsUpdated({
            sessions: [previewStub],
            currentSession: DESIGN_PREVIEW_LOCAL_SESSION,
            currentTranscript: [],
        })
    )
}

function resolveSessionIdFromNewSessionResponse(data) {
    if (!data || typeof data !== 'object') return null
    return data.id ?? data.sessionId ?? null
}

const useBotsService = () => {
    const botState = useSelector(state => state.bot)
    const dispatch = useDispatch()
    const reduxStore = useStore()

    const syncSessions = async () => {
        try {
            const config = getCurrentConfig()
            if (!config) return []
            if (config.isDesignPreview) return []
            if (!config.agentId) throw new Error('agentId is required in bot config')

            const sessions = await api.getAllSessions(config.agentId)
            const previousSessions = reduxStore.getState().bot.sessions
            const mappedSessions = Array.isArray(sessions)
                ? sessions.map((session) => {
                    const mapped = mapSessionMetadata(session)
                    if (!hasSessionName(mapped.name)) {
                        const previous = previousSessions.find((s) => s.id === mapped.id)
                        if (hasSessionName(previous?.name)) {
                            mapped.name = previous.name
                        }
                    }
                    return mapped
                })
                : []

            if (mappedSessions.length > 0) {
                const sorted = sortSessions(mappedSessions)
                const currentSession = sorted[0].id
                const interactions = await api.getSession(currentSession)
                dispatch(sessionsUpdated({
                    sessions: sortSessions(mappedSessions),
                    currentSession,
                    currentTranscript: mapInteractionsToTranscript(interactions),
                }))
            }
            return mappedSessions
        } catch (e) {
            console.log(`couldn't fetch sessions: ${e}`)
            return []
        }
    }

    const newSession = async () => {
        const config = getCurrentConfig()
        try {
            if (!config?.isDesignPreview && !config?.agentId) {
                throw new Error('agentId is required in bot config')
            }

            const data = await api.newSession()
            const sessionId = resolveSessionIdFromNewSessionResponse(data)

            if (config?.isDesignPreview) {
                if (sessionId) {
                    const previewStub = buildDesignPreviewSessionStub(sessionId, config)
                    dispatch(
                        sessionsUpdated({
                            sessions: [previewStub],
                            currentSession: sessionId,
                            currentTranscript: [],
                        })
                    )
                } else {
                    console.warn("isDesignPreview: newSession response missing session id — using local preview session")
                    dispatchDesignPreviewLocalSession(config, dispatch)
                }
                return
            }

            if (sessionId) {
                await syncSessions()
                return
            }

            await syncSessions()
        } catch (e) {
            console.log(`couldn't create session: ${e}`)
            if (config?.isDesignPreview) {
                dispatchDesignPreviewLocalSession(config, dispatch)
            }
        }
    }

    const ensureSession = async () => {
        const existing = reduxStore.getState().bot.currentSession
        if (existing) return existing

        const config = getCurrentConfig()
        if (config?.isDesignPreview) {
            dispatchDesignPreviewLocalSession(config, dispatch)
            return reduxStore.getState().bot.currentSession
        }

        await newSession()
        return reduxStore.getState().bot.currentSession
    }

    const requestSessionName = async (chatSessionId, transcript) => {
        if (namingInFlight.has(chatSessionId)) return
        namingInFlight.add(chatSessionId)

        try {
            const transcriptText = formatTranscriptForNaming(transcript)
            if (!transcriptText.trim()) return

            // Re-check: another path may have named the session meanwhile
            const sessionRow = reduxStore.getState().bot.sessions.find((s) => s.id === chatSessionId)
            if (hasSessionName(sessionRow?.name)) return

            const data = await api.updateSessionName(chatSessionId, transcriptText)
            const title = data?.name ?? data?.Name ?? null
            if (!hasSessionName(title)) return

            dispatch(sessionNameUpdated({ id: chatSessionId, name: String(title).trim() }))
        } catch (e) {
            console.warn(`couldn't name session: ${e}`)
        } finally {
            namingInFlight.delete(chatSessionId)
        }
    }

    const maybeRequestSessionName = (chatSessionId, transcript) => {
        try {
            const config = getCurrentConfig()
            if (config?.isDesignPreview) return

            const sessionId = chatSessionId || reduxStore.getState().bot.currentSession
            if (!sessionId || isLocalDesignPreviewSession(sessionId)) return

            const { sessions } = reduxStore.getState().bot
            const sessionRow = sessions.find((s) => s.id === sessionId)
            if (hasSessionName(sessionRow?.name)) return

            const transcriptForNaming = transcript || reduxStore.getState().bot.currentTranscript
            const modelTurns = countCompletedModelTurns(transcriptForNaming)
            if (modelTurns < 2 || modelTurns % 2 !== 0) return

            void requestSessionName(sessionId, transcriptForNaming)
        } catch (e) {
            console.warn(`couldn't start session naming: ${e}`)
        }
    }
    
    const sendMessage = async (text) => {
        const trimmed = String(text ?? '').trim()
        if (!trimmed) return

        try {
            await ensureSession()

            let { currentSession, currentTranscript } = reduxStore.getState().bot
            const cfg = getCurrentConfig()

            if (!currentSession) {
                console.log("couldn't send message: no current session")
                return
            }

            const chatSessionId = currentSession

            const newTranscript = [...currentTranscript, { sender: "USER", text: trimmed }]
            dispatch(messagePosted({ currentTranscript: newTranscript }))

            if (cfg?.isDesignPreview && isLocalDesignPreviewSession(chatSessionId)) {
                dispatch(startStreamingResponse({ userText: trimmed }))
                dispatch(finalizeStreamingResponse({
                    userText: trimmed,
                    modelMessage: "תצוגת עיצוב — התשובה תופיע בצ'אט האמיתי לאחר פרסום.",
                }))
                return
            }

            dispatch(startStreamingResponse({ userText: trimmed }))

            let streamCompleted = false
            let streamSucceeded = false

            await api.sendMessageStream(trimmed, chatSessionId, {
                onEvent: (event) => {
                    const payload = event?.payload || {}
                    switch (event?.type) {
                        case 'thinking_delta':
                            dispatch(appendStreamDelta({
                                userText: trimmed,
                                streamType: 'thinking_delta',
                                text: payload.text || payload.content || '',
                            }))
                            break
                        case 'message_delta':
                            dispatch(appendStreamDelta({
                                userText: trimmed,
                                streamType: 'message_delta',
                                text: payload.text || payload.content || '',
                            }))
                            break
                        case 'reset':
                            dispatch(resetStreamBuffers({ userText: trimmed }))
                            break
                        case 'done':
                            streamCompleted = true
                            streamSucceeded = true
                            dispatch(finalizeStreamingResponse({
                                userText: trimmed,
                                modelMessage: payload.text ?? payload.modelMessage ?? payload.ModelMessage ?? null,
                            }))
                            break
                        case 'error':
                            streamCompleted = true
                            dispatch(markStreamingFailed({
                                userText: trimmed,
                                errorCode: payload.errorCode,
                                source: payload.source,
                            }))
                            break
                        default:
                            break
                    }
                },
            })

            if (!streamCompleted) {
                dispatch(markStreamingFailed({
                    userText: trimmed,
                    errorCode: 'UNKNOWN_RUNTIME_ERROR',
                    source: 'Runtime',
                }))
            }

            if (streamSucceeded) {
                const namingTranscript = reduxStore.getState().bot.currentTranscript
                maybeRequestSessionName(chatSessionId, namingTranscript)
            }

            if (!cfg?.isDesignPreview) {
                await syncSessions()
            }
        } catch (e) {
            console.log(`couldn't send message: ${e}`)
            dispatch(markStreamingFailed({
                userText: trimmed,
                errorCode: 'UNKNOWN_RUNTIME_ERROR',
                source: 'Runtime',
            }))
        }
    }
    
    const switchSession = async (id) => {
        try {
            const interactions = await api.getSession(id)
            dispatch(currentUpdated({
                currentSession: id,
                currentTranscript: mapInteractionsToTranscript(interactions),
            }))
        } catch (e) {
            console.log(`couldn't switch sessions: ${e}`)
        }
    }

    const getCurrentSession = () => {
        return botState.currentSession
    }

    const getAllSessions = () => {
        return botState.sessions
    }

    const getCurrentTranscript = () => {
        return botState.currentTranscript
    }

    const sortSessions = (sessions) => {
        let sorted = sessions.slice().sort((a, b) => {
            var keyA = new Date(a.lastUpdated)
            var keyB = new Date(b.lastUpdated)
            if (keyA < keyB) return 1;
            if (keyA > keyB) return -1;
            return 0;
          })
        return sorted
    }

    const isPendingResponse = () => {
        return botState.pendingResponse
    }

    return {
        syncSessions: syncSessions,
        newSession: newSession,
        ensureSession: ensureSession,
        sendMessage: sendMessage,
        switchSession: switchSession,
        getCurrentSession: getCurrentSession,
        getAllSessions: getAllSessions,
        getCurrentTranscript: getCurrentTranscript,
        isPendingResponse: isPendingResponse
    }
}

export default useBotsService
